import type { ReleaseMark } from '@/admin/dashboards/chartViewModel';

import { formatReleasePromptForDisplay } from './releasePromptDisplay';

/**
 * Metrics week key for a release: the ISO Monday on or before the release calendar date.
 * Returns null when that Monday is not on the chart axis (no neighbour snap).
 */
export function snapReleaseToWeekX(at: string, weekXs: string[]): string | null {
  if (!weekXs.length) return null;
  const calendar = at.slice(0, 10);
  const parts = calendar.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [year, month, day] = parts;
  const releaseUtc = Date.UTC(year, month - 1, day);
  const weekday = new Date(releaseUtc).getUTCDay();
  const daysFromMonday = weekday === 0 ? 6 : weekday - 1;
  const mondayUtc = releaseUtc - daysFromMonday * 86_400_000;
  const mondayStr = new Date(mondayUtc).toISOString().slice(0, 10);
  return weekXs.includes(mondayStr) ? mondayStr : null;
}

export type GroupedWeekRelease = {
  weekKey: string;
  weekIndex: number;
  weekCount: number;
  prompts: string[];
  /** Display names joined for one shared label, e.g. "formative_hint, evaluate_draft". */
  names: string;
};

/**
 * One marker per week. Releases on the same Monday-on-or-before week share that marker,
 * and the label lists their names.
 */
export function groupReleasesByWeek(
  releaseLines: ReleaseMark[] | undefined,
  weekXs: string[],
): GroupedWeekRelease[] {
  const buckets = new Map<string, ReleaseMark[]>();
  (releaseLines ?? []).forEach((release) => {
    const weekKey = snapReleaseToWeekX(release.at, weekXs);
    if (!weekKey) return;
    const list = buckets.get(weekKey) ?? [];
    list.push(release);
    buckets.set(weekKey, list);
  });

  const weekCount = weekXs.length;
  return weekXs.flatMap((weekKey, weekIndex) => {
    const list = buckets.get(weekKey);
    if (!list?.length) return [];
    const sorted = [...list].sort((a, b) => a.prompt.localeCompare(b.prompt));
    return [{
      weekKey,
      weekIndex,
      weekCount,
      prompts: sorted.map((release) => release.prompt),
      names: sorted.map((release) => formatReleasePromptForDisplay(release.prompt)).join(', '),
    }];
  });
}
